import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material'
import { ContentCopy } from '@mui/icons-material'
import { ReactElement, useEffect, useRef, useState } from 'react'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import DangerousHtml from 'src/components/common/dangerous-html'
import { TextContent } from 'src/v3/core/types'
import { extractPlainText } from 'src/v3/create/v5/course/utils/html-extraction'
import { modalInputSx, modalPaperProps } from 'src/v3/utilities/modal-styles'
import {
  createCourseShowNotes,
  selectCourseV2ShowNotesByCourseID,
} from 'src/v3/content-pipeline/course-pipeline.slice'

type Props = {
  isOpen: boolean
  onClose: () => void
  courseID: string
  textContent: TextContent
  onMigrateSuccess: () => void
}

export default function MigrateTextContentModal({
  isOpen,
  onClose,
  courseID,
  textContent,
  onMigrateSuccess,
}: Props): ReactElement {
  let dispatch = useAppDispatch()
  let showNotesList = useAppSelector(
    selectCourseV2ShowNotesByCourseID(courseID),
  )
  let [name, setName] = useState(textContent.name)
  let [description, setDescription] = useState(textContent.description ?? '')
  let [isPending, setIsPending] = useState(false)
  let expectedCountRef = useRef<number | null>(null)

  useEffect(() => {
    if (
      expectedCountRef.current !== null &&
      showNotesList.length >= expectedCountRef.current
    ) {
      // eslint-disable-next-line fp/no-mutation
      expectedCountRef.current = null
      setIsPending(false)
      onMigrateSuccess()
      onClose()
    }
  }, [showNotesList, onMigrateSuccess, onClose])

  let handleCopy = () => {
    let plain = extractPlainText(textContent.content)
    navigator.clipboard.writeText(plain)
  }

  let handleMigrate = () => {
    // eslint-disable-next-line fp/no-mutation
    expectedCountRef.current = showNotesList.length + 1
    setIsPending(true)
    dispatch(
      createCourseShowNotes({
        courseId: courseID,
        payload: { name, description: description || null },
      }),
    )
  }

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      fullWidth
      slotProps={{
        paper: modalPaperProps,
      }}
      maxWidth="md"
    >
      <DialogTitle>Migrate Text Content to Show Notes</DialogTitle>
      <DialogContent>
        <Typography
          color="text.secondary"
          variant="body2"
          sx={{
            mb: 3,
          }}
        >
          This legacy Text Content is not compatible with v2. Copy the content
          below into the new Show Notes editor that will be created. Saving will
          create a new Show Notes record linked to this course.
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 3,
          }}
        >
          {/* Left: read-only legacy content */}
          <Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 1,
              }}
            >
              <Typography variant="subtitle2">Legacy Content</Typography>
              <Button
                size="small"
                startIcon={<ContentCopy />}
                onClick={handleCopy}
              >
                Copy plain text
              </Button>
            </Box>
            <Box
              sx={{
                p: 2,
                borderRadius: 1,
                bgcolor: 'background.paper',
                maxHeight: 360,
                overflowY: 'auto',
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <DangerousHtml htmlVal={textContent.content} isEncoded={true} />
            </Box>
          </Box>

          {/* Right: new show notes fields */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              New Show Notes
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                size="small"
                fullWidth
                required
                sx={modalInputSx}
              />
              <TextField
                label="Description (optional)"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                size="small"
                fullWidth
                multiline
                rows={3}
                sx={modalInputSx}
              />
              <Typography color="text.secondary" variant="caption">
                After saving, the Show Notes editor will open so you can add
                content blocks.
              </Typography>
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleMigrate}
          disabled={!name.trim() || isPending}
          startIcon={isPending ? <CircularProgress size={16} /> : undefined}
        >
          {isPending ? 'Creating…' : 'Create Show Notes'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
